/**
 * Component: Quick view
 * ------------------------------------------------------------------------------
 * A quick view component.
 *
 * @namespace quickView
 */

import axios from 'axios';
import loaders from '../helpers/loaders';
import {on, serialize} from '../helpers/utils';
import Modal from '../components/modal';
import ProductImages from '../components/product-images';
import ProductForm from './product-form';

/**
 * DOM selectors.
 */
const selectors = {
  addToCart: '[js-quick-view="addToCart"]',
  viewport: '[js-quick-view="viewport"]',
  body: '[js-quick-view="body"]',
  toggle: '[js-quick-view="toggle"]',
};

/**
 * Create a new quick view object.
 */
export default () => {

  const namespace = 'quick-view';
  const modal = '.modal--quick-view';

  /**
   * DOM node selectors.
   */
  const nodeSelectors = {
    body: document.querySelector(selectors.body),
    toggleSelectors: document.querySelector(selectors.toggleSelectors),
    viewport: document.querySelector(selectors.viewport),
    toggle: document.querySelectorAll(selectors.toggle),
  };

  /**
   * Initialise component.
   */
  function init() {
    if (!nodeSelectors.toggle) {return;}

    setEventListeners();
    setEventBusListeners();
    Modal(modal).init();

    console.log(`🏅 Quick View: Loaded`)
  }

  /**
   * Set event listeners.
   */
  function setEventListeners() {
    nodeSelectors.toggle.forEach((item) => {
      on('click', item, (event) => handleQuickViewClick(event, item));
    });
  }

  /**
   * Set EventBus listeners on component.
   */
  function setEventBusListeners() {
    Karmoon.EventBus.listen('QuickView:open', (response) => handleQuickViewEvent(response));
  }

  /**
   * Handle quick view toggle click event.
   * @param {Event} event - Click event.
   */
  function handleQuickViewClick(event, item) {
    event.preventDefault();
    const productHandle = event.target.dataset.quickViewUrl;
    Karmoon.EventBus.emit('QuickView:open', productHandle);
  }

  /**
   * Handle quick view eventbus event.
   * @param {String} handle - The product handle to quick view.
   */
  function handleQuickViewEvent(handle) {
    setLoadingState();
    toggleQuickView(handle);
  }

  /**
   * Toggle quick view modal after fetching data from toggle target.
   * @param {String} handle - The product handle to quick view.
   */
  function toggleQuickView(handle) {

    Modal(modal).modalOpen();

    axios.get(getProductUrl(handle))
      .then((response) => {
        nodeSelectors.body.innerHTML = response.data;
        removeLoadingState();

        ProductImages().init();
        initProductForm();
        setAddToCartEvents();

        return response;
      })
      .catch((error) => error);
  }

  /**
   * Set loading state.
   */
  function setLoadingState() {
    nodeSelectors.body.innerHTML = loaders.loader;
  }

  /**
   * Remove loading state.
   */
  function removeLoadingState() {
    return;
  }

  /**
   * Initialise product form.
   */
  function initProductForm() {
    ProductForm({
      container: 'quick-view',
      enableSwatches: true,
    }).init();
  }

  /**
   * Set click events on buttons to post form data.
   */
  function setAddToCartEvents() {
    on('click', document.querySelector(selectors.addToCart), (event) => handleAddToCart(event));
  }

  /**
   * Handle add to cart event and set loading state.
   * @param {Object} event - Click and key down event.
   */
  function handleAddToCart(event) {
    event.preventDefault();
    const target = event.currentTarget;
    setAddingToCartState(target);

    axios.post('/cart/add.js', serialize(target.closest('form')))
      .then((response) => {
        setSuccessState(target, response);

        return response;
      })
      .catch((error) => {
        return error;
      });
  }

  /**
   * Set success state when item has been successfully added.
   * @param {HTMLElement} target - Add to cart button.
   * @param {Object} response - Successful JSON response from add.js.
   */
  function setSuccessState(target, response) {
    setDefaultState(target);

    window.setTimeout(() => {
      Karmoon.EventBus.emit('AddToCart:itemAdded', response);
    }, 500);

    Modal(modal).closeModal();
    nodeSelectors.body.innerHTML = '';
  }

  /**
   * Reset the loading states back to default.
   * @param {Object} target clicked add to cart button in DOM.
   */
  function setDefaultState(target) {
    target.innerText = theme.strings.addToCart;
  }

  /**
   * Set adding state to add to cart button.
   * @param {Object} target clicked add to cart button in DOM.
   */
  function setAddingToCartState(target) {
    target.innerText = theme.strings.addingToCart;
  }

  /**
   * Get quick view product template url.
   * @param {String} handle - The product handle to quick view.
   * @returns {String}
   */
  function getProductUrl(handle) {
    return `/products/${handle}?view=quick-view`;
  }

  /**
   * Expose public interface.
   */
  return Object.freeze({
    init,
  });
};
